/**
 * Confirmação forte: só libera depois que a pessoa digita o nome do alvo.
 *
 * Reservada a DUAS ações — remover membro e transferir propriedade. Confirmação
 * em fluxo que se repete vira tapinha automático e para de proteger; por isso
 * ela não aparece em nenhum outro lugar do painel.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Dialog, Field, Input } from '@mgdlead/ui';

import { FormAlert } from '@mgdlead/ui';
import { humanizeError } from '../lib/errors';

export interface ConfirmByNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** Texto que a pessoa precisa digitar — normalmente o nome do membro. */
  confirmationText: string;
  confirmLabel: string;
  onConfirm: () => Promise<unknown>;
}

export function ConfirmByNameDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmationText,
  confirmLabel,
  onConfirm,
}: ConfirmByNameDialogProps) {
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // reabrir com o campo já preenchido derrubaria a proteção inteira
  useEffect(() => {
    if (open) {
      setTyped('');
      setError(null);
    }
  }, [open]);

  const matches = typed.trim().toLocaleLowerCase() === confirmationText.trim().toLocaleLowerCase();

  const handleConfirm = () => {
    if (!matches || pending) return;
    setPending(true);
    setError(null);
    void onConfirm()
      .then(() => {
        onOpenChange(false);
      })
      .catch((cause: unknown) => {
        setError(humanizeError(cause));
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={!matches} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <FormAlert>{error}</FormAlert> : null}

        <Field
          label={
            <>
              Digite <span className="font-mono font-medium">{confirmationText}</span> para
              confirmar
            </>
          }
          hint="A conferência ignora maiúsculas e minúsculas."
        >
          {(control) => (
            <Input
              {...control}
              value={typed}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              onChange={(event) => setTyped(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
